import { MicrophoneIcon, MicrophoneSlashIcon, PhoneDisconnectIcon } from "@phosphor-icons/react";
import { Button } from "../components/Button";
import { useRadio } from "./useRadio";
import { inVoiceLabel, speakersLine } from "./voiceState";

// Team voice at the bottom center of the home scene: one open-mic channel.
// Out of voice, the white-ring orb joins (mic open). In voice, it toggles
// mute (M does too) and a HUD button beside it leaves. White rings pulse
// while you speak, red rings while someone else does; one mono line names
// who is speaking, and a tiny roster shows who is in voice.

export function RadioOrb() {
  const radio = useRadio();
  const { mode, inVoice, muted, speaking, meId } = radio;
  const offline = radio.conn !== "open";
  const state = { online: radio.online, voice: radio.voice, you: { in_voice: inVoice, muted } };
  const selfSpeaking = inVoice && !muted && speaking.has(meId);
  const othersSpeaking = inVoice && radio.voice.some((p) => p.id !== meId && speaking.has(p.id));
  const who = speakersLine(state, speaking, meId);

  const line =
    radio.message ??
    (inVoice
      ? who
        ? `${who}${othersSpeaking && radio.needsGesture ? " · tap anywhere to hear" : ""}`
        : null
      : mode === "joining"
        ? "joining voice"
        : inVoiceLabel(state));

  const label = mode === "out" ? "tap to join voice" : mode === "joining" ? "opening the mic" : muted ? "muted · m to unmute" : "mic open · m to mute";

  return (
    <div className={`radio radio--${mode}${selfSpeaking ? " radio--self" : ""}${othersSpeaking ? " radio--others" : ""}${offline ? " radio--offline" : ""}`}>
      {radio.voice.length ? (
        <ul className="roster" aria-label="In voice">
          {radio.voice.map((p) => {
            const on = inVoice && speaking.has(p.id) && !(p.id === meId && muted);
            return (
              <li key={p.id} className={`${on ? "on" : ""}${p.muted ? " muted" : ""}`.trim() || undefined}>
                {p.muted ? <MicrophoneSlashIcon className="roster__muted" size={11} weight="light" aria-hidden="true" /> : <span className="roster__dot" aria-hidden="true" />}
                {p.id === meId ? "you" : p.name.toLowerCase()}
                <span className="sr-only">{p.muted ? ", muted" : on ? ", speaking" : ""}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
      <p className="radio__line" aria-live="polite">
        {line ? (
          <>
            <span className="prompt" aria-hidden="true">
              &gt;{" "}
            </span>
            {line}
          </>
        ) : null}
      </p>
      <div className="radio__row">
        <div className="radio__call">
          {inVoice ? (
            <Button size="sm" icon={PhoneDisconnectIcon} onClick={radio.leave}>
              leave
            </Button>
          ) : null}
        </div>
        <button
          type="button"
          className="orb"
          aria-label={!inVoice ? "Join voice. Opens your microphone." : muted ? "Unmute microphone (M)" : "Mute microphone (M)"}
          aria-pressed={inVoice ? muted : undefined}
          aria-describedby="orb-hint"
          disabled={offline || mode === "joining"}
          onClick={() => (inVoice ? radio.toggleMute() : radio.join())}
        >
          <span className="orb__pulse" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {inVoice && muted ? (
            <MicrophoneSlashIcon className="orb__mic" size={22} weight="light" aria-hidden="true" />
          ) : (
            <MicrophoneIcon className="orb__mic" size={22} weight="light" aria-hidden="true" />
          )}
        </button>
        <span />
      </div>
      <span className="radio__label" id="orb-hint">
        {label}
      </span>
    </div>
  );
}
